import { useEffect, useRef, useState } from "react";
import api, { fileUrl } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Building2, Upload, Trash2, ImageIcon, Mail, Server, Send, ShieldCheck,
  CheckCircle2, AlertTriangle, Lock, Trash, Radar, Copy, XCircle, Info as InfoIcon,
  BellRing,
} from "lucide-react";
import PlatformBrandingSection from "@/components/PlatformBrandingSection";
import TenantPlansSection from "@/components/TenantPlansSection";

const emptySmtp = {
  enabled: true, host: "", port: 587, security: "starttls",
  username: "", password: "", from_email: "", from_name: "Mailroom OS",
};

export default function SettingsPage() {
  const { user, tenant, refresh } = useAuth();
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  // SMTP state
  const [smtp, setSmtp] = useState(emptySmtp);
  const [smtpLoaded, setSmtpLoaded] = useState(false);
  const [smtpConfigured, setSmtpConfigured] = useState(false);
  const [passwordSet, setPasswordSet] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  // Deliverability state
  const [deliv, setDeliv] = useState(null);
  const [delivDomain, setDelivDomain] = useState("");
  const [delivLoading, setDelivLoading] = useState(false);

  // SLA escalation state
  const [sla, setSla] = useState({ sla_level1_email: "", sla_level2_email: "", sla_level3_email: "" });
  const [slaHours, setSlaHours] = useState({ inward: 24, outward: 8 });
  const [slaSaving, setSlaSaving] = useState(false);
  const canManageSla = user?.role === "company_admin" || user?.role === "super_admin";

  useEffect(() => {
    if (!canManageSla) return;
    (async () => {
      try {
        const { data } = await api.get("/settings/sla");
        setSla({
          sla_level1_email: data.sla_level1_email || "",
          sla_level2_email: data.sla_level2_email || "",
          sla_level3_email: data.sla_level3_email || "",
        });
        if (data.sla_hours) setSlaHours(data.sla_hours);
      } catch (_e) { /* ignore */ }
    })();
  }, [canManageSla]);

  const saveSla = async (e) => {
    e.preventDefault();
    setSlaSaving(true);
    try {
      await api.put("/settings/sla", sla);
      toast.success("SLA escalation contacts saved", {
        description: "Orange alerts → L1 · Red alerts → L2 (+ L3 CC).",
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSlaSaving(false); }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings/smtp");
        if (data.configured) {
          setSmtp({
            enabled: data.enabled !== false,
            host: data.host || "",
            port: data.port || 587,
            security: data.security || "starttls",
            username: data.username || "",
            password: "",
            from_email: data.from_email || "",
            from_name: data.from_name || "Mailroom OS",
          });
          setSmtpConfigured(true);
          setPasswordSet(!!data.password_set);
        }
        setTestTo(user?.email || "");
      } catch (_e) { /* ignore */ }
      finally { setSmtpLoaded(true); }
    })();
  }, [user?.email]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo updated");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const removeLogo = async () => {
    if (!window.confirm("Remove company logo?")) return;
    try { await api.delete("/settings/logo"); toast.success("Logo removed"); await refresh(); }
    catch (_e) { toast.error("Failed"); }
  };

  const saveSmtp = async (e) => {
    e.preventDefault();
    if (!smtp.host || !smtp.from_email) return toast.error("Host and From email are required");
    setSaving(true);
    try {
      const payload = { ...smtp };
      // Preserve existing password if user didn't type a new one
      if (!payload.password) payload.password = passwordSet ? "••••••••" : "";
      const { data } = await api.put("/settings/smtp", payload);
      toast.success("SMTP relay saved", { description: "Future emails will use this server first." });
      setSmtpConfigured(true);
      setPasswordSet(!!data.password_set);
      setSmtp((s) => ({ ...s, password: "" }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const runTest = async () => {
    if (!testTo) return toast.error("Enter a recipient email to test");
    if (!smtpConfigured) return toast.error("Save your SMTP settings first");
    setTesting(true);
    try {
      await api.post("/settings/smtp/test", { to: testTo });
      toast.success(`Test sent to ${testTo}`, { description: "Check inbox / spam." });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally { setTesting(false); }
  };

  const disable = async () => {
    if (!window.confirm("Disable self-hosted SMTP? Notifications will fall back to the Emergent-managed relay.")) return;
    try {
      await api.delete("/settings/smtp");
      toast.success("SMTP relay removed", { description: "Notifications now use the platform relay." });
      setSmtp(emptySmtp);
      setSmtpConfigured(false);
      setPasswordSet(false);
    } catch (_e) { toast.error("Failed"); }
  };

  const runDeliverability = async () => {
    const target = delivDomain || (smtp.from_email || "").split("@")[1] || "";
    if (!target) return toast.error("Enter a domain (or set From-email above first)");
    setDelivLoading(true);
    setDeliv(null);
    try {
      const { data } = await api.get(`/settings/smtp/deliverability?domain=${encodeURIComponent(target)}`);
      setDeliv(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lookup failed");
    } finally { setDelivLoading(false); }
  };

  const copyToClipboard = async (txt) => {
    try {
      await navigator.clipboard.writeText(txt);
      toast.success("Copied to clipboard");
    } catch { toast.error("Copy failed"); }
  };

  const P = (k, v) => setSmtp((s) => ({ ...s, [k]: v }));

  return (
    <div className="p-8 space-y-6 max-w-5xl">
      <header>
        <div className="overline flex items-center gap-2"><Building2 size={12} /> Company workspace</div>
        <h1 className="text-4xl font-display mt-1">Settings.</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Configure how {tenant?.name || "your workspace"} appears in the console, on delivery receipts, and how outbound notifications are delivered.
        </p>
      </header>

      {/* Platform white-labeling (super_admin only) */}
      {user?.role === "super_admin" && <PlatformBrandingSection />}
      {user?.role === "super_admin" && <TenantPlansSection />}

      {/* Branding */}
      <section className="border border-border rounded-md bg-card p-6 card-elev">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="overline">Branding</div>
            <h2 className="font-display text-xl mt-1">Company logo</h2>
            <p className="text-sm text-muted-foreground mt-1 max-w-md">
              PNG, JPG or WEBP. Square icons or horizontal wordmarks both work — aspect ratio is preserved. Renders on the sidebar and on every signed delivery-receipt PDF.
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="h-24 min-w-[6rem] max-w-[16rem] px-3 rounded-md border border-border bg-muted/30 flex items-center justify-center overflow-hidden" data-testid="logo-preview">
              {tenant?.logo_url ? (
                <img
                  src={fileUrl(tenant.logo_url)}
                  alt="Company logo"
                  className="block h-full w-auto max-w-full object-contain"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              ) : (
                <ImageIcon size={28} className="text-muted-foreground" />
              )}
            </div>
            <div className="flex flex-col gap-2">
              <input ref={fileRef} type="file" accept="image/*" hidden data-testid="logo-file"
                onChange={(e) => upload(e.target.files?.[0])} />
              <Button data-testid="logo-upload-btn" onClick={() => fileRef.current?.click()} disabled={uploading}
                className="rounded-md bg-primary text-primary-foreground shadow-brut-orange">
                <Upload size={14} className="mr-2" /> {uploading ? "Uploading…" : "Upload logo"}
              </Button>
              {tenant?.logo_url && (
                <Button data-testid="logo-remove-btn" onClick={removeLogo} variant="outline" className="rounded-md">
                  <Trash2 size={14} className="mr-2" /> Remove
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* SLA escalation contacts */}
      {canManageSla && (
        <section className="border border-border rounded-md bg-card p-6 card-elev" data-testid="sla-section">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="overline flex items-center gap-2"><BellRing size={12} /> SLA escalation</div>
              <h2 className="font-display text-xl mt-1">Escalation contacts</h2>
              <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                Open parcels are colour-graded by age against the target SLA
                (<span className="mono">inward {slaHours.inward}h · outward {slaHours.outward}h</span>).
                When a parcel enters the Orange band (≥60% of SLA elapsed) Level 1 is emailed once.
                When it crosses into Red (SLA breached) Level 2 is emailed once, with Level 3 CC'd if set.
              </p>
            </div>
          </div>
          <form onSubmit={saveSla} className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
            <label className="block">
              <span className="overline flex items-center gap-1">
                <span className="inline-block w-2 h-2 rounded-full bg-amber-500" /> Level 1 · Orange
              </span>
              <Input
                data-testid="sla-l1"
                type="email"
                value={sla.sla_level1_email}
                onChange={(e) => setSla({ ...sla, sla_level1_email: e.target.value })}
                placeholder="ops.lead@company.com"
                className="mt-1 rounded-md"
              />
            </label>
            <label className="block">
              <span className="overline flex items-center gap-1">
                <span className="inline-block w-2 h-2 rounded-full bg-red-600" /> Level 2 · Red
              </span>
              <Input
                data-testid="sla-l2"
                type="email"
                value={sla.sla_level2_email}
                onChange={(e) => setSla({ ...sla, sla_level2_email: e.target.value })}
                placeholder="ops.manager@company.com"
                className="mt-1 rounded-md"
              />
            </label>
            <label className="block">
              <span className="overline flex items-center gap-1">
                <span className="inline-block w-2 h-2 rounded-full bg-slate-900" /> Level 3 · CC on Red
              </span>
              <Input
                data-testid="sla-l3"
                type="email"
                value={sla.sla_level3_email}
                onChange={(e) => setSla({ ...sla, sla_level3_email: e.target.value })}
                placeholder="director@company.com (optional)"
                className="mt-1 rounded-md"
              />
            </label>
            <div className="md:col-span-3 flex justify-end">
              <Button
                data-testid="sla-save"
                type="submit"
                disabled={slaSaving}
                className="rounded-md bg-primary text-primary-foreground shadow-brut-orange"
              >
                <Send size={14} className="mr-2" /> {slaSaving ? "Saving…" : "Save escalation contacts"}
              </Button>
            </div>
          </form>
        </section>
      )}

      {/* SMTP Relay */}
      <section className="border border-border rounded-md bg-card p-6 card-elev" data-testid="smtp-section">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="overline flex items-center gap-2"><Server size={12} /> Outbound email · Self-hosted relay</div>
            <h2 className="font-display text-xl mt-1">Mail relay server</h2>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
              Route notifications (parcel arrivals, priority alerts, pickup requests, reports) through your own SMTP server for full delivery control and DMARC alignment.
              When enabled, your relay is tried first; if unreachable, the platform relay is used as fallback.
            </p>
          </div>
          <div className="min-w-[160px] text-right">
            {smtpLoaded && (smtpConfigured && smtp.enabled ? (
              <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded border border-green-500 bg-green-50 text-green-700 font-semibold" data-testid="smtp-status">
                <CheckCircle2 size={12} /> Active
              </span>
            ) : smtpConfigured ? (
              <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded border border-amber-500 bg-amber-50 text-amber-800 font-semibold" data-testid="smtp-status">
                <AlertTriangle size={12} /> Configured · disabled
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded border border-slate-300 bg-slate-50 text-slate-600 font-semibold" data-testid="smtp-status">
                <Mail size={12} /> Using platform relay
              </span>
            ))}
          </div>
        </div>

        <form onSubmit={saveSmtp} className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="flex items-center gap-2 md:col-span-2 text-sm border border-border rounded-md p-3 bg-muted/20">
            <input type="checkbox" data-testid="smtp-enabled" checked={smtp.enabled}
              onChange={(e) => P("enabled", e.target.checked)}
              className="accent-[#BE1E1E] w-4 h-4" />
            <span className="font-semibold">Use this SMTP relay for notifications</span>
            <span className="text-xs text-muted-foreground ml-2">Uncheck to save credentials but keep the platform relay in use.</span>
          </label>

          <SmtpField label="SMTP Host" required v={smtp.host} set={(v) => P("host", v)} testid="smtp-host"
            placeholder="smtp.example.com" mono />
          <SmtpField label="Port" required v={smtp.port} set={(v) => P("port", parseInt(v) || 0)} testid="smtp-port"
            type="number" mono placeholder="587" />

          <div>
            <span className="overline">Security</span>
            <Select value={smtp.security} onValueChange={(v) => P("security", v)}>
              <SelectTrigger data-testid="smtp-security" className="rounded-md border-input mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="starttls">STARTTLS (recommended · port 587)</SelectItem>
                <SelectItem value="ssl">SSL / TLS (port 465)</SelectItem>
                <SelectItem value="none">None (unencrypted · not recommended)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <SmtpField label="From email" required v={smtp.from_email} set={(v) => P("from_email", v)} testid="smtp-from"
            type="email" placeholder="mailroom@yourcompany.com" mono />

          <SmtpField label="Username (SMTP AUTH)" v={smtp.username} set={(v) => P("username", v)} testid="smtp-username"
            placeholder="Usually your email address" mono />
          <div>
            <span className="overline flex items-center gap-1.5">
              <Lock size={11} /> Password / App password
              {passwordSet && <span className="text-[10px] uppercase tracking-widest bg-green-50 border border-green-500 text-green-700 px-1.5 py-0.5 rounded ml-1">SAVED</span>}
            </span>
            <Input data-testid="smtp-password" type="password"
              value={smtp.password}
              placeholder={passwordSet ? "•••••••• (leave blank to keep)" : "Enter password"}
              onChange={(e) => P("password", e.target.value)}
              className="mt-1 rounded-md border-input focus-visible:ring-primary font-mono" />
            <div className="text-[10px] text-muted-foreground mt-1">
              Encrypted at rest. For Gmail / Google Workspace use an <a className="text-primary underline" href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer">App Password</a>. For Outlook 365 use an app password too.
            </div>
          </div>

          <SmtpField label="From name" v={smtp.from_name} set={(v) => P("from_name", v)} testid="smtp-from-name"
            placeholder="Mailroom OS" />

          <div className="md:col-span-2 flex items-center gap-2 flex-wrap">
            <Button data-testid="smtp-save" type="submit" disabled={saving}
              className="rounded-md bg-primary text-primary-foreground hover-brut shadow-brut">
              <ShieldCheck size={14} className="mr-2" /> {saving ? "Saving…" : (smtpConfigured ? "Update SMTP" : "Save SMTP")}
            </Button>
            {smtpConfigured && (
              <Button type="button" data-testid="smtp-disable" onClick={disable} variant="outline"
                className="rounded-md">
                <Trash size={14} className="mr-2" /> Remove config
              </Button>
            )}
          </div>
        </form>

        {/* Test panel */}
        <div className="mt-6 border-t border-border pt-5">
          <div className="overline flex items-center gap-2"><Send size={12} /> Send test email</div>
          <p className="text-xs text-muted-foreground mt-1">Sends a formatted test to verify the connection, TLS, AUTH and headers end-to-end.</p>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <Input data-testid="smtp-test-to" value={testTo} onChange={(e) => setTestTo(e.target.value)}
              placeholder="Recipient email" className="max-w-sm rounded-md" />
            <Button data-testid="smtp-test-btn" type="button" onClick={runTest} disabled={testing || !smtpConfigured}
              className="rounded-md bg-primary text-primary-foreground hover-brut shadow-brut">
              <Send size={14} className="mr-2" /> {testing ? "Sending…" : "Send test"}
            </Button>
            {!smtpConfigured && (
              <span className="text-[11px] text-muted-foreground">Save settings first, then test.</span>
            )}
          </div>
        </div>

        {/* Common presets */}
        <div className="mt-6 border-t border-border pt-5">
          <div className="overline">Common presets</div>
          <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
            <Preset name="Gmail / Google Workspace" host="smtp.gmail.com" port={587} sec="starttls"
              note="Use an App Password." />
            <Preset name="Microsoft 365 / Outlook" host="smtp.office365.com" port={587} sec="starttls"
              note="Requires app password if MFA is enabled." />
            <Preset name="Amazon SES (EU)" host="email-smtp.eu-west-1.amazonaws.com" port={587} sec="starttls"
              note="Use SES SMTP credentials." />
          </div>
        </div>
      </section>

      {/* Deliverability — SPF / DKIM / DMARC */}
      <section className="border border-border rounded-md bg-card p-6 card-elev" data-testid="deliverability-section">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="overline flex items-center gap-2"><Radar size={12} /> Deliverability · SPF / DKIM / DMARC</div>
            <h2 className="font-display text-xl mt-1">Domain authentication</h2>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
              We check whether the sending domain of your <b>From-email</b> has valid SPF, DKIM and DMARC DNS records published.
              Getting these right is the #1 reason self-hosted notifications land in inbox instead of spam.
            </p>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-2 flex-wrap">
          <Input data-testid="deliv-domain" value={delivDomain}
            onChange={(e) => setDelivDomain(e.target.value)}
            placeholder={smtp.from_email ? `Uses ${smtp.from_email.split("@")[1] || ""} by default` : "e.g. yourcompany.com"}
            className="max-w-sm rounded-md" />
          <Button data-testid="deliv-check-btn" onClick={runDeliverability} disabled={delivLoading}
            className="rounded-md bg-primary text-primary-foreground hover-brut shadow-brut">
            <Radar size={14} className="mr-2" /> {delivLoading ? "Checking DNS…" : "Run deliverability check"}
          </Button>
        </div>

        {deliv && (
          <div className="mt-6 space-y-4" data-testid="deliv-results">
            <div className="text-[11px] text-muted-foreground">
              Domain: <b className="mono">{deliv.domain}</b>
              {deliv.provider && <> · Detected provider: <b>{deliv.provider}</b></>}
              <> · Checked at <span className="mono">{deliv.checked_at?.slice(0, 19).replace("T", " ")} UTC</span></>
            </div>

            <DnsCard title="SPF" subtitle="Sender Policy Framework — authorises which servers may send mail on behalf of your domain."
              status={deliv.spf.status} record={deliv.spf.record} notes={deliv.spf.notes}
              suggested={deliv.spf.suggested} onCopy={copyToClipboard} testid="deliv-spf" />

            <DnsCard title="DKIM" subtitle="Cryptographic signature key — proves the message wasn't tampered with in transit."
              status={deliv.dkim.status}
              record={deliv.dkim.found?.length ? deliv.dkim.found.map(f => `[${f.selector}]  ${f.record}`).join("\n") : null}
              notes={[...deliv.dkim.notes, deliv.dkim.next_step]}
              suggested={deliv.dkim.suggested} onCopy={copyToClipboard} testid="deliv-dkim" />

            <DnsCard title="DMARC" subtitle="Policy telling receivers what to do when SPF or DKIM fail (and where to send reports)."
              status={deliv.dmarc.status} record={deliv.dmarc.record} notes={deliv.dmarc.notes}
              suggested={deliv.dmarc.suggested} onCopy={copyToClipboard} testid="deliv-dmarc" />

            <div className="text-[11px] text-muted-foreground border-t border-border pt-3">
              <InfoIcon size={11} className="inline mr-1" />
              After you publish TXT records at your DNS provider, changes can take 5–30 minutes to propagate. Run the check again to verify.
            </div>
          </div>
        )}
      </section>

      {/* Company info readonly */}
      <section className="border border-border rounded-md bg-card p-6 card-elev">
        <div className="overline">Company profile</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-3">
          <InfoRow label="Name" value={tenant?.name} />
          <InfoRow label="Code" value={tenant?.code} mono />
          <InfoRow label="Status" value={tenant?.active === false ? "Inactive" : "Active"} />
        </div>
      </section>
    </div>
  );
}

function SmtpField({ label, v, set, testid, type = "text", required, placeholder, mono }) {
  return (
    <label className="block">
      <span className="overline">{label}{required && <span className="text-primary"> *</span>}</span>
      <Input data-testid={testid} value={v ?? ""} onChange={(e) => set(e.target.value)}
        required={required} type={type} placeholder={placeholder}
        className={`mt-1 rounded-md border-input focus-visible:ring-primary ${mono ? "font-mono" : ""}`} />
    </label>
  );
}

function Preset({ name, host, port, sec, note }) {
  return (
    <div className="border border-border rounded-md p-3 bg-muted/20">
      <div className="font-semibold">{name}</div>
      <div className="mono text-[11px] text-muted-foreground mt-1">{host}:{port} · {sec}</div>
      <div className="text-[11px] text-muted-foreground mt-1">{note}</div>
    </div>
  );
}

const InfoRow = ({ label, value, mono }) => (
  <div>
    <div className="overline">{label}</div>
    <div className={`text-lg mt-1 ${mono ? "mono" : "font-semibold"}`}>{value || "—"}</div>
  </div>
);

function DnsCard({ title, subtitle, status, record, notes, suggested, onCopy, testid }) {
  const STYLES = {
    pass: { icon: CheckCircle2, tone: "border-green-500 bg-green-50 text-green-800", pill: "bg-green-600 text-white", label: "PASS" },
    warn: { icon: AlertTriangle, tone: "border-amber-500 bg-amber-50 text-amber-800", pill: "bg-amber-500 text-white", label: "WARN" },
    fail: { icon: XCircle, tone: "border-primary bg-red-50 text-primary", pill: "bg-primary text-primary-foreground", label: "FAIL" },
  };
  const S = STYLES[status] || STYLES.warn;
  const Icon = S.icon;
  return (
    <div className={`border rounded-md p-4 ${S.tone}`} data-testid={testid}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Icon size={16} />
            <h3 className="font-display text-lg">{title}</h3>
            <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded ${S.pill}`}>{S.label}</span>
          </div>
          <p className="text-xs opacity-80 mt-1">{subtitle}</p>
        </div>
      </div>

      {record && (
        <div className="mt-3">
          <div className="overline">Current record</div>
          <pre className="mt-1 whitespace-pre-wrap break-all font-mono text-[11px] bg-white/60 border border-white/60 rounded p-2 text-slate-800">{record}</pre>
        </div>
      )}

      {(notes || []).length > 0 && (
        <ul className="mt-3 text-xs list-disc pl-4 space-y-1 opacity-90">
          {notes.filter(Boolean).map((n, i) => <li key={i}>{n}</li>)}
        </ul>
      )}

      {suggested && (
        <div className="mt-3 border border-dashed border-current/40 rounded p-3 bg-white/60">
          <div className="overline flex items-center justify-between">
            <span>Recommended DNS entry</span>
            <button onClick={() => onCopy(suggested.value)} data-testid={`${testid}-copy`}
              className="text-[10px] inline-flex items-center gap-1 px-2 py-0.5 rounded border border-current hover:bg-current hover:text-white transition-colors">
              <Copy size={10} /> Copy value
            </button>
          </div>
          <div className="mt-2 grid grid-cols-[70px_1fr] gap-x-3 gap-y-1 text-xs">
            <div className="mono opacity-70">Type</div><div className="mono font-semibold">{suggested.type}</div>
            <div className="mono opacity-70">Host</div><div className="mono font-semibold break-all">{suggested.host}</div>
            <div className="mono opacity-70">Value</div><div className="mono font-semibold break-all">{suggested.value}</div>
          </div>
        </div>
      )}
    </div>
  );
}
